import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  FormControl,
  FormHelperText,
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

const ContactDropdownField = ({
  formData,
  onChange,
  formContext,
  rawErrors = [],
}) => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  useEffect(() => {
    if (Array.isArray(formContext?.formData?.contacts)) {
      setContacts(formContext.formData.contacts);
    }
  }, [formContext?.formData?.contacts]);

  useEffect(() => {
    if (typeof formData === "number" && contacts[formData]) {
      setSelectedContact(contacts[formData]);
    } else {
      setSelectedContact(null);
    }
  }, [formData, contacts]);

  useEffect(() => {
    let selected = null;

    // Try to select by index if formData is a number or numeric string
    const index = !isNaN(formData) ? parseInt(formData, 10) : null;
    if (index !== null && contacts[index]) {
      selected = contacts[index];
    }

    // If index-based match fails, try email match (for edit scenario)
    if (
      !selected &&
      formContext?.formData?.billingInfoSection0?.billingContactEmail
    ) {
      selected = contacts.find(
        (c) =>
          c.contactEmail ===
          formContext.formData.billingInfoSection0.billingContactEmail
      );
    }

    setSelectedContact(selected || null);

    // Set the index in the Autocomplete if only email matched (for consistency)
    if (selected && (formData === "" || formData === undefined)) {
      const matchedIndex = contacts.findIndex(
        (c) => c.contactEmail === selected.contactEmail
      );
      onChange(matchedIndex);
    }
  }, [
    formData,
    contacts,
    formContext?.formData?.billingInfoSection0?.billingContactEmail,
  ]);

  const clearBillingFields = () => {
    formContext.setFormData?.((prev) => ({
      ...prev,
      billingInfoSection0: {
        ...prev.billingInfoSection0,
        selectedContactId: "",
        billingContactFirstName: "",
        billingContactLastName: "",
        billingContactPhone: "",
        billingContactEmail: "",
      },
    }));
    formContext.setFormDataVersion(formContext.formDataVersion + 1);
  };

  const handleChange = (_, selectedContactObj) => {
    if (!selectedContactObj && selectedContactObj === null) {
      setSelectedContact(null);
      onChange(""); // Reset formData for this field
      clearBillingFields();
      return;
    }
    const selectedIdx = contacts.findIndex(
      (c) => c.contactEmail === selectedContactObj.contactEmail
    );

    setSelectedContact(selectedContactObj);
    onChange(selectedIdx);

    const updatedFields = {
      billingContactFirstName: selectedContactObj.contactFirstName || "",
      billingContactLastName: selectedContactObj.contactLastName || "",
      billingContactPhone: selectedContactObj.contactPhoneNumber || "",
      billingContactEmail: selectedContactObj.contactEmail || "",
      selectedContactId: selectedIdx,
    };

    formContext.setFormData?.((prev) => ({
      ...prev,
      billingInfoSection0: {
        ...(prev.billingInfoSection0 || {}),
        ...updatedFields,
      },
    }));
    formContext.setFormDataVersion(formContext.formDataVersion + 1);
  };

  return (
    <>
      <FormControl fullWidth error={rawErrors?.length > 0}>
        <Autocomplete
          value={selectedContact}
          onChange={handleChange}
          options={contacts}
          getOptionLabel={(contact) =>
            contact
              ? `${contact.contactFirstName || ""} ${
                  contact.contactLastName || ""
                }`.trim()
              : ""
          }
          isOptionEqualToValue={(option, value) =>
            option?.contactEmail === value?.contactEmail
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select billing contact *"
              variant="outlined"
              error={rawErrors?.length > 0}
            />
          )}
        />
        {rawErrors?.length > 0 && (
          <FormHelperText>{rawErrors[0]}</FormHelperText>
        )}
      </FormControl>

      {selectedContact && (
        <Card variant="outlined" sx={{ my: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <PersonIcon fontSize="large" color="action" />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedContact.contactFirstName}{" "}
                  {selectedContact.contactLastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedContact.contactPhoneNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedContact.contactEmail}
                </Typography>
                {Array.isArray(selectedContact.contactRoles) &&
                  selectedContact.contactRoles.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedContact.contactRoles.join(", ")}
                    </Typography>
                  )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ContactDropdownField;
